import { useObservability } from '../../helpers/observability';
import { SellerFlowAddressInput, SellerFlowAddressInputProps } from './SellerFlowAddressInput';
import { TrackingTaxonomy, AddressSearchLocation } from '../globals';
import { CTAProps } from '@opendoor/bricks/complex/CtaInput';

export type AddressSearchProp = Pick<CTAProps, 'actionText' | 'actionTextSmall' | 'size'> &
  Pick<
    SellerFlowAddressInputProps,
    | 'channel'
    | 'autoFocus'
    | 'onFocus'
    | 'paddingLevel'
    | 'hideLabel'
    | 'placeholderText'
    | 'labelProps'
  > & {
    trackingTaxonomy: TrackingTaxonomy;
    inputLocation: AddressSearchLocation;
    displayResultsAbove?: boolean;
    analyticsPrefix?: string;
    /**
     * Whether to track an impression event when the input first enters the viewport
     */
    trackInputImpression?: boolean;
    prefilledAddress?: string;
  };

const AddressSearch = ({
  trackingTaxonomy,
  inputLocation,
  autoFocus,
  onFocus,
  paddingLevel,
  actionText,
  actionTextSmall,
  placeholderText,
  size,
  hideLabel,
  displayResultsAbove = false,
  analyticsPrefix = 'cosmos',
  labelProps,
  prefilledAddress,
  channel,
}: AddressSearchProp) => {
  const { trackEvent } = useObservability();

  const onQueryChange = (newQuery?: string, oldQuery?: string) => {
    if (oldQuery === '' && newQuery && newQuery.length > 0) {
      // TODO: remove categoryOverride
      // observability is initialized with 'cosmos' as the category for all events
      trackEvent('typing', trackingTaxonomy, {}, { categoryOverride: 'address-input' });
    }
  };

  return (
    <SellerFlowAddressInput
      analyticsName={`${analyticsPrefix}-address-input`}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus || false}
      inputLocation={inputLocation}
      trackingTaxonomy={trackingTaxonomy}
      onQueryChange={onQueryChange}
      hideLabel={hideLabel}
      designStyle="rounded"
      showShadow
      size={size}
      onFocus={onFocus}
      paddingLevel={paddingLevel}
      displayAbove={displayResultsAbove}
      placeholderText={placeholderText}
      initialInput={prefilledAddress ?? undefined}
      forceNewAddressInput={prefilledAddress !== undefined}
      showCta
      channel={channel}
      ctaProps={{
        actionText,
        actionTextSmall,
        'aria-label': 'Submit address',
        ...(prefilledAddress !== undefined
          ? {
              prefillBehavior: {
                editCtaText: 'Update address',
                iconProps: {
                  name: 'chevron-right',
                  color: 'brand50',
                  size: 12,
                },
                textProps: {
                  color: 'brand50',
                },
              },
            }
          : {}),
      }}
      product="SELL_DIRECT"
      labelProps={labelProps}
    />
  );
};

export default AddressSearch;
